import { wrapIndex } from '../core/math';
import type { Corner } from './brakingzones';
import type { Track } from './generate';
import { heightAtLateral } from './lookup';
import { PointGrid } from './pointgrid';

/**
 * The one thing beside a circuit that says *which* circuit it is.
 *
 * Everything else the renderers draw is derived — kerbs from curvature,
 * signage from an RNG seeded on the track key, run-off from an authored span.
 * That is enough to make a lap feel like a lap and not nearly enough to make
 * Zandvoort feel like Zandvoort. A landmark is the exception: a hand-placed
 * object that exists because it exists at that circuit and nowhere else.
 *
 * Placement lives here rather than in either renderer, for the same reason
 * sponsor placement does (`docs/3d-assets.md`, "Both renderers must agree") —
 * an object has to be at the same metre of track in the overhead view as in
 * 3D. Sizes are in *game* metres and set by eye, not real dimensions scaled
 * down: the circuits are already shrunk to 0.2–0.59 of life, and a faithfully
 * scaled ferris wheel next to an 8 m kart track reads as a scale error rather
 * than as Suzuka. Same standard as `RealCircuit.elevation` — first-pass
 * figures meant to be refined by feel.
 *
 * Nothing here is read by the physics, the speed profile or the lap timer, so
 * adding, moving or removing a landmark cannot move a lap time and a dated
 * circuit still regenerates byte-identically (`CLAUDE.md`).
 */

export type LandmarkKind =
  /** Zandvoort's Dutch national motorsport monument: a stainless rear wing
   *  and two granite wheels on a granite plinth, by the main entrance. */
  | 'monument'
  /** The Red Bull Ring's bull, charging through an arch — 17 m of rusted
   *  steel, built to be legible from the motorway two kilometres away. */
  | 'bull'
  /** A big wheel, as at Suzuka. */
  | 'wheel'
  /** A pit and timing building. */
  | 'tower'
  /** A raked spectator stand. See `LandmarkSpec.roof` for the shape over it. */
  | 'grandstand'
  /** Monza's derelict banked oval. */
  | 'banking';

/**
 * What sits on top of a grandstand. Most circuits get a plain cantilever slab;
 * the two that are architecturally famous get theirs. Shanghai's canopy is a
 * membrane cut as lotus leaves, and Sepang's main stand is a tensile roof
 * shaped after a hibiscus, with the palm-leaf canopies beside it — both by
 * Tilke, and both the thing people picture when the circuit is named.
 */
export type GrandstandRoof = 'flat' | 'lotus' | 'hibiscus';

/** What a circuit authors. See `RealCircuit.landmarks`. */
export interface LandmarkSpec {
  kind: LandmarkKind;
  /** Shown in the overhead view and read by assistive tech. */
  name: string;
  /** Where along the lap it sits, as a fraction from the start/finish line. */
  at: number;
  /** Metres from the centerline. Positive is left of the racing direction. */
  offset: number;
  /** Size multiplier on the kind's own base dimensions. */
  scale?: number;
  /** Extra rotation past "square to the track", in radians. */
  yaw?: number;
  /** Roof shape, for `kind: 'grandstand'`. Defaults to a plain slab. */
  roof?: GrandstandRoof;
  /**
   * Put it on whichever side of the circuit has more room, ignoring the sign
   * of `offset`. Main grandstands want the open side, and which side that is
   * depends on how the layout folds — something the trace knows and the
   * author does not.
   */
  roomier?: boolean;
}

/** A spec resolved against a built track. */
export interface PlacedLandmark {
  kind: LandmarkKind;
  name: string;
  x: number;
  y: number;
  /** Ground height at its feet. */
  height: number;
  /** Unit tangent of the track alongside it, pointing the way karts travel.
   *  A vector rather than an angle, for the same reason `SponsorBillboard`
   *  carries one: the track layer stays free of trig, and the renderer's
   *  facing maths cannot divide by zero. */
  tx: number;
  ty: number;
  side: 1 | -1;
  scale: number;
  yaw: number;
  roof: GrandstandRoof;
  /** Plan-view footprint radius in metres, for the overhead marker and for
   *  keeping the thing off the circuit. */
  radius: number;
}

/** Footprint of each kind at `scale: 1`, in game metres. */
const RADIUS: Record<LandmarkKind, number> = {
  monument: 4,
  bull: 8,
  wheel: 13,
  tower: 5,
  grandstand: 16,
  banking: 26,
};

/** Air between a landmark's footprint and the barrier it stands behind. */
const STANDOFF = 2.5;

/**
 * How much room a point has before it touches the circuit — the whole lap,
 * with no exemption for the stretch the landmark belongs to.
 *
 * An earlier version skipped everything within 60 m of lap distance, on the
 * theory that a landmark cannot be "on" the road it is standing beside. That
 * is true on a straight and false at a hairpin, which doubles back inside the
 * exempt window and put Estoril's grandstand 6 m from a road the check had
 * been told to ignore. No exemption is needed: a landmark is placed beyond its
 * own barrier plus its own footprint, so its own stretch clears this on its
 * own terms.
 */

/** How far past the barrier a landmark may be pushed while still being *beside*
 *  the circuit rather than lost in a field somewhere. */
const MAX_PUSH = 40;

/** Lap distance two stands must be apart, so they read as separate enclosures
 *  around the circuit rather than one long terrace. */
const STAND_SPACING = 150;

/**
 * Stands at the corners worth watching, on top of whatever the circuit
 * authored.
 *
 * A circuit does not put its spectators only on the pit straight. They go
 * where the racing happens, which is the end of the long braking zones — so
 * the corners are ranked by how much speed they take out of a lap, biggest
 * drop first, and the stand goes on the *outside*, which is both where the
 * room is and where you would sit to watch someone try a move up the inside.
 * Ranking on the circuit's own corner data rather than on an authored list is
 * what puts stands on different sides as the lap turns one way and then the
 * other, without anyone deciding that circuit by circuit.
 *
 * Derived, not authored, and cosmetic like everything else here — none of it
 * can move a lap time.
 */
function deriveGrandstands(track: Track, corners: Corner[], taken: number[]): LandmarkSpec[] {
  const n = track.center.length;
  const gapSamples = STAND_SPACING / track.spacing;
  const wanted = Math.max(1, Math.min(4, Math.floor(track.length / 650)));

  const ranked = [...corners].sort((a, b) => b.vEntry - b.vApex - (a.vEntry - a.vApex));
  const out: LandmarkSpec[] = [];
  const used = [...taken];

  for (const c of ranked) {
    if (out.length >= wanted) break;
    const far = used.every((i) => {
      const gap = Math.min(wrapIndex(c.apexIndex - i, n), wrapIndex(i - c.apexIndex, n));
      return gap >= gapSamples;
    });
    if (!far) continue;
    used.push(c.apexIndex);

    // A corner that takes more speed out of the lap earns a bigger stand.
    const drop = c.vEntry - c.vApex;
    out.push({
      kind: 'grandstand',
      name: `Turn ${c.number} grandstand`,
      at: c.apexIndex / n,
      // Outside of the corner. `roomier` may still overrule it where the
      // outside is hemmed in by another part of the lap.
      offset: 26 * -c.direction,
      scale: 0.7 + Math.min(0.45, drop / 45),
      roomier: true,
    });
  }
  return out;
}

export function placeLandmarks(track: Track, corners: Corner[] = []): PlacedLandmark[] {
  const authored = track.landmarks;
  // A generated daily layout has no identity to carry and gets no scenery, so
  // its look is unchanged by any of this.
  if (!authored?.length) return [];
  const specs = [
    ...authored,
    ...deriveGrandstands(track, corners, authored.map((a) => wrapIndex(Math.round(a.at * track.center.length), track.center.length))),
  ];
  const n = track.center.length;
  const out: PlacedLandmark[] = [];
  const grid = new PointGrid(track.center, 24);

  for (const spec of specs) {
    const index = wrapIndex(Math.round(spec.at * n), n);
    const scale = spec.scale ?? 1;
    const radius = RADIUS[spec.kind] * scale;
    const needed = radius + track.width / 2 + track.kerbWidth + STANDOFF;

    /**
     * The best spot this side can offer, and how much room is left over.
     *
     * Walking outward until something fits cannot be done blindly: a circuit
     * is a closed loop, so keep pushing and the landmark eventually arrives on
     * the far side of it — which is how Estoril's grandstand ended up 2.4 m
     * from the road. Scan a bounded range instead, keep the roomiest spot
     * found, and stop early once there is genuinely enough space.
     */
    const reach = (s: 1 | -1): { distance: number; margin: number } => {
      const barrier = (s > 0 ? track.barriers.left : track.barriers.right).dist[index];
      const nx = track.normal[index].x * s;
      const ny = track.normal[index].y * s;
      const from = Math.max(Math.abs(spec.offset), barrier + radius + STANDOFF);
      let best = { distance: from, margin: -Infinity };
      for (let d = from; d <= from + MAX_PUSH; d += 2) {
        const gap = grid.nearestWithin(track.center[index].x + nx * d, track.center[index].y + ny * d, needed + 1);
        const margin = gap - needed;
        if (margin > best.margin) best = { distance: d, margin };
        if (margin >= 0) break;
      }
      return best;
    };

    const preferred: 1 | -1 = spec.offset >= 0 ? 1 : -1;
    let side = preferred;
    let spot = reach(preferred);
    if (spec.roomier) {
      const flip: 1 | -1 = preferred === 1 ? -1 : 1;
      const other = reach(flip);
      // The authored side is a preference, not a tie-break: take it whenever
      // it fits at all, and only cross the circuit when it does not. A corner
      // stand asks for the outside of the corner for a reason, and swapping it
      // to the inside because there happened to be a metre more room there
      // would put the crowd behind the apex looking at the back of the action.
      const better = spot.margin < 0 ? other.margin > spot.margin : false;
      if (better) {
        side = flip;
        spot = other;
      }
    }
    // Nothing fits here. Drop it rather than stand it in the road — the same
    // rule the sponsor placer follows for a board it cannot fit
    // (`docs/3d-assets.md`): a compact circuit carries less scenery, not
    // misplaced scenery.
    if (spot.margin < 0) continue;
    const distance = spot.distance;
    const nx = track.normal[index].x * side;
    const ny = track.normal[index].y * side;

    const ahead = track.center[wrapIndex(index + 1, n)];
    const behind = track.center[wrapIndex(index - 1, n)];
    const span = Math.hypot(ahead.x - behind.x, ahead.y - behind.y) || 1;

    out.push({
      kind: spec.kind,
      name: spec.name,
      x: track.center[index].x + nx * distance,
      y: track.center[index].y + ny * distance,
      // The terrain shelf holds flat past the kerb, so a landmark stands on
      // the same level the run-off beside it does rather than floating over a
      // gradient it is nowhere near.
      height: heightAtLateral(track, index, distance * side),
      tx: (ahead.x - behind.x) / span,
      ty: (ahead.y - behind.y) / span,
      side,
      scale,
      yaw: spec.yaw ?? 0,
      roof: spec.roof ?? 'flat',
      radius,
    });
  }
  return out;
}
